import { createSlice } from "@reduxjs/toolkit";
import {
  getFeaturedVideos,
  getVideosList,
  getCategoryVideos,
  fetchVideo,
  getVideoPlaybackUrl,
  getVideoCategories,
  getVideoSubcategories,
  getRelatedVideos,
  getParentCategoryVideos,
  toggleVideoLike,
  toggleVideoReactionLike,
  toggleVideoCompletion,
  purchaseProduct,
} from "../actions/videosActions";

const initialState = {
  featured: { data: [], meta: null, isLoading: false, error: null },
  list: { data: [], meta: null, isLoading: false, error: null },
  categoryList: { data: [], meta: null, isLoading: false, error: null },
  categories: { data: [], allIconUrl: null, isLoading: false, error: null },
  subcategories: { data: [], isLoading: false, error: null },
  related: { data: [], isLoading: false, error: null },
  parentList: { data: [], meta: null, isLoading: false, error: null },
  videoDetail: { data: null, isLoading: false, error: null },
  playbackUrl: { url: null, playback: null, otherDevice: false, isLoading: false, error: null },
  purchase: { data: null, isLoading: false, error: null },
  _likePending: false,
  _reactionLikePending: false,
  _completionPending: false,
};

/**
 * Helper: toggle saved/favorite state on a video in a data array.
 */
const toggleSavedInArray = (arr, videoId, newLiked) => {
  const idx = arr.findIndex((v) => v.id === videoId);
  if (idx === -1) return;
  const attrs = arr[idx].attributes;
  attrs.liked = newLiked;
};

const adjustLikesTotalInArray = (arr, videoId, delta) => {
  const idx = arr.findIndex((v) => v.id === videoId);
  if (idx === -1) return;
  const attrs = arr[idx].attributes;
  attrs.likes_total = Math.max(0, (attrs.likes_total ?? 0) + delta);
};

const videosSlice = createSlice({
  name: "videos",
  initialState,
  reducers: {
    clearFeatured: (state) => {
      state.featured = initialState.featured;
    },
    clearList: (state) => {
      state.list = initialState.list;
    },
    clearCategoryList: (state) => {
      state.categoryList = initialState.categoryList;
    },
    clearVideoDetail: (state) => {
      state.videoDetail = initialState.videoDetail;
    },
    clearPlaybackUrl: (state) => {
      state.playbackUrl = initialState.playbackUrl;
    },
    clearCategories: (state) => {
      state.categories = initialState.categories;
    },
    clearSubcategories: (state) => {
      state.subcategories = initialState.subcategories;
    },
    clearRelated: (state) => {
      state.related = initialState.related;
    },
    clearParentList: (state) => {
      state.parentList = initialState.parentList;
    },
    clearPurchase: (state) => {
      state.purchase = initialState.purchase;
    },
  },
  extraReducers: (builder) => {
    // ── Featured videos ──
    builder.addCase(getFeaturedVideos.pending, (state, action) => {
      if (!action.meta.arg?.page || action.meta.arg.page === 1) {
        state.featured.isLoading = true;
      }
      state.featured.error = null;
    });
    builder.addCase(getFeaturedVideos.fulfilled, (state, action) => {
      state.featured.isLoading = false;
      const { data, meta, page } = action.payload;
      if (!page || page === 1) {
        state.featured.data = data ?? [];
      } else {
        state.featured.data = [...state.featured.data, ...(data ?? [])];
      }
      state.featured.meta = meta ?? null;
    });
    builder.addCase(getFeaturedVideos.rejected, (state, action) => {
      state.featured.isLoading = false;
      state.featured.error = action.error.message;
    });

    // ── Videos list ──
    builder.addCase(getVideosList.pending, (state, action) => {
      if (!action.meta.arg?.page || action.meta.arg.page === 1) {
        state.list.isLoading = true;
      }
      state.list.error = null;
    });
    builder.addCase(getVideosList.fulfilled, (state, action) => {
      state.list.isLoading = false;
      const { data, meta, page } = action.payload;
      if (!page || page === 1) {
        state.list.data = data ?? [];
      } else {
        state.list.data = [...state.list.data, ...(data ?? [])];
      }
      state.list.meta = meta ?? null;
    });
    builder.addCase(getVideosList.rejected, (state, action) => {
      state.list.isLoading = false;
      state.list.error = action.error.message;
    });

    // ── Category videos (VideoCategory page feed — separate from `list`) ──
    builder.addCase(getCategoryVideos.pending, (state, action) => {
      if (!action.meta.arg?.page || action.meta.arg.page === 1) {
        state.categoryList.isLoading = true;
      }
      state.categoryList.error = null;
    });
    builder.addCase(getCategoryVideos.fulfilled, (state, action) => {
      state.categoryList.isLoading = false;
      const { data, meta, page } = action.payload;
      if (!page || page === 1) {
        state.categoryList.data = data ?? [];
      } else {
        state.categoryList.data = [
          ...state.categoryList.data,
          ...(data ?? []),
        ];
      }
      state.categoryList.meta = meta ?? null;
    });
    builder.addCase(getCategoryVideos.rejected, (state, action) => {
      state.categoryList.isLoading = false;
      state.categoryList.error = action.error.message;
    });

    // ── Categories ──
    builder.addCase(getVideoCategories.pending, (state) => {
      state.categories.isLoading = true;
      state.categories.error = null;
    });
    builder.addCase(getVideoCategories.fulfilled, (state, action) => {
      state.categories.isLoading = false;
      state.categories.data = action.payload.data ?? [];
      // Global icon for the synthetic "Tutti" pill, returned as collection meta.
      // Defensive trim of a stray trailing quote (e.g. "...jpg%22") so a
      // malformed stored value still renders — backend should fix at source.
      const rawAllIcon = action.payload?.meta?.all_category_icon_url;
      state.categories.allIconUrl =
        typeof rawAllIcon === "string"
          ? rawAllIcon.trim().replace(/(?:%22|")+$/i, "") || null
          : null;
    });
    builder.addCase(getVideoCategories.rejected, (state, action) => {
      state.categories.isLoading = false;
      state.categories.error = action.error.message;
    });

    // ── Subcategories (separate slice to avoid flash on navigation) ──
    builder.addCase(getVideoSubcategories.pending, (state) => {
      state.subcategories.isLoading = true;
      state.subcategories.error = null;
    });
    builder.addCase(getVideoSubcategories.fulfilled, (state, action) => {
      state.subcategories.isLoading = false;
      state.subcategories.data = action.payload.data ?? [];
    });
    builder.addCase(getVideoSubcategories.rejected, (state, action) => {
      state.subcategories.isLoading = false;
      state.subcategories.error = action.error.message;
    });

    // ── Related videos (correlati slider — no pagination) ──
    builder.addCase(getRelatedVideos.pending, (state) => {
      state.related.isLoading = true;
      state.related.error = null;
    });
    builder.addCase(getRelatedVideos.fulfilled, (state, action) => {
      state.related.isLoading = false;
      state.related.data = action.payload?.data ?? [];
    });
    builder.addCase(getRelatedVideos.rejected, (state, action) => {
      state.related.isLoading = false;
      state.related.error = action.error.message;
    });

    // ── Parent category videos (altri video infinite feed) ──
    builder.addCase(getParentCategoryVideos.pending, (state, action) => {
      if (!action.meta.arg?.page || action.meta.arg.page === 1) {
        state.parentList.isLoading = true;
      }
      state.parentList.error = null;
    });
    builder.addCase(getParentCategoryVideos.fulfilled, (state, action) => {
      state.parentList.isLoading = false;
      const { data, meta, page } = action.payload;
      if (!page || page === 1) {
        state.parentList.data = data ?? [];
      } else {
        state.parentList.data = [...state.parentList.data, ...(data ?? [])];
      }
      state.parentList.meta = meta ?? null;
    });
    builder.addCase(getParentCategoryVideos.rejected, (state, action) => {
      state.parentList.isLoading = false;
      state.parentList.error = action.error.message;
    });

    // ── Video detail ──
    builder.addCase(fetchVideo.pending, (state) => {
      state.videoDetail.isLoading = true;
      state.videoDetail.error = null;
    });
    builder.addCase(fetchVideo.fulfilled, (state, action) => {
      state.videoDetail.isLoading = false;
      // API may return { data: [...] } array or a single object
      const payload = action.payload;
      if (Array.isArray(payload?.data)) {
        state.videoDetail.data = payload.data[0] ?? null;
      } else if (payload?.data) {
        state.videoDetail.data = payload.data;
      } else {
        state.videoDetail.data = payload;
      }
    });
    builder.addCase(fetchVideo.rejected, (state, action) => {
      state.videoDetail.isLoading = false;
      state.videoDetail.error = action.error.message;
    });

    // ── Playback URL ──
    builder.addCase(getVideoPlaybackUrl.pending, (state) => {
      state.playbackUrl.isLoading = true;
      state.playbackUrl.error = null;
    });
    builder.addCase(getVideoPlaybackUrl.fulfilled, (state, action) => {
      state.playbackUrl.isLoading = false;
      state.playbackUrl.url = action.payload.url ?? null;
      // Native playback block (hls_url / mp4_url / dimensions). Null on
      // older backends, where the page falls back to the Vimeo embed.
      state.playbackUrl.playback = action.payload.playback ?? null;
      // "Guarda su un altro dispositivo": true on movement videos.
      state.playbackUrl.otherDevice = action.payload.other_device === true;
    });
    builder.addCase(getVideoPlaybackUrl.rejected, (state, action) => {
      state.playbackUrl.isLoading = false;
      state.playbackUrl.error = action.error.message;
    });

    // ── Toggle like (optimistic) ──
    builder.addCase(toggleVideoLike.pending, (state, action) => {
      state._likePending = true;
      const { id, liked } = action.meta.arg;
      const newLiked = !liked;
      toggleSavedInArray(state.featured.data, id, newLiked);
      toggleSavedInArray(state.list.data, id, newLiked);
      toggleSavedInArray(state.categoryList.data, id, newLiked);
      toggleSavedInArray(state.related.data, id, newLiked);
      toggleSavedInArray(state.parentList.data, id, newLiked);
      // Also update videoDetail if it matches
      if (state.videoDetail.data?.id === id) {
        const attrs = state.videoDetail.data.attributes ?? state.videoDetail.data;
        attrs.liked = newLiked;
      }
    });
    builder.addCase(toggleVideoLike.fulfilled, (state) => {
      state._likePending = false;
    });
    builder.addCase(toggleVideoLike.rejected, (state, action) => {
      state._likePending = false;
      // Revert optimistic update
      const { id, liked } = action.meta.arg;
      toggleSavedInArray(state.featured.data, id, liked);
      toggleSavedInArray(state.list.data, id, liked);
      toggleSavedInArray(state.categoryList.data, id, liked);
      toggleSavedInArray(state.related.data, id, liked);
      toggleSavedInArray(state.parentList.data, id, liked);
      if (state.videoDetail.data?.id === id) {
        const attrs = state.videoDetail.data.attributes ?? state.videoDetail.data;
        attrs.liked = liked;
      }
    });

    // ── Toggle detail thumbs-up reaction (optimistic count only) ──
    builder.addCase(toggleVideoReactionLike.pending, (state, action) => {
      state._reactionLikePending = true;
      const { id, reactionLiked } = action.meta.arg;
      const delta = reactionLiked ? -1 : 1;
      adjustLikesTotalInArray(state.featured.data, id, delta);
      adjustLikesTotalInArray(state.list.data, id, delta);
      adjustLikesTotalInArray(state.categoryList.data, id, delta);
      adjustLikesTotalInArray(state.related.data, id, delta);
      adjustLikesTotalInArray(state.parentList.data, id, delta);
      if (state.videoDetail.data?.id === id) {
        const attrs = state.videoDetail.data.attributes ?? state.videoDetail.data;
        attrs.likes_total = Math.max(0, (attrs.likes_total ?? 0) + delta);
      }
    });
    builder.addCase(toggleVideoReactionLike.fulfilled, (state) => {
      state._reactionLikePending = false;
    });
    builder.addCase(toggleVideoReactionLike.rejected, (state, action) => {
      state._reactionLikePending = false;
      const { id, reactionLiked } = action.meta.arg;
      const delta = reactionLiked ? 1 : -1;
      adjustLikesTotalInArray(state.featured.data, id, delta);
      adjustLikesTotalInArray(state.list.data, id, delta);
      adjustLikesTotalInArray(state.categoryList.data, id, delta);
      adjustLikesTotalInArray(state.related.data, id, delta);
      adjustLikesTotalInArray(state.parentList.data, id, delta);
      if (state.videoDetail.data?.id === id) {
        const attrs = state.videoDetail.data.attributes ?? state.videoDetail.data;
        attrs.likes_total = Math.max(0, (attrs.likes_total ?? 0) + delta);
      }
    });

    // ── Toggle detail "completed" marker (per-user; component holds the
    // optimistic boolean, slice just guards concurrent taps + keeps detail
    // in sync on success) ──
    builder.addCase(toggleVideoCompletion.pending, (state) => {
      state._completionPending = true;
    });
    builder.addCase(toggleVideoCompletion.fulfilled, (state, action) => {
      state._completionPending = false;
      const { id, completed } = action.payload || {};
      if (state.videoDetail.data?.id === id) {
        const attrs =
          state.videoDetail.data.attributes ?? state.videoDetail.data;
        attrs.completed = completed;
      }
    });
    builder.addCase(toggleVideoCompletion.rejected, (state) => {
      state._completionPending = false;
    });

    // ── Purchase ──
    builder.addCase(purchaseProduct.pending, (state) => {
      state.purchase.isLoading = true;
      state.purchase.error = null;
    });
    builder.addCase(purchaseProduct.fulfilled, (state, action) => {
      state.purchase.isLoading = false;
      state.purchase.data = action.payload;
    });
    builder.addCase(purchaseProduct.rejected, (state, action) => {
      state.purchase.isLoading = false;
      state.purchase.error = action.error.message;
    });
  },
});

export const {
  clearFeatured,
  clearList,
  clearCategoryList,
  clearVideoDetail,
  clearPlaybackUrl,
  clearCategories,
  clearSubcategories,
  clearRelated,
  clearParentList,
  clearPurchase,
} = videosSlice.actions;

export default videosSlice.reducer;
